// Error handling utilities for network failures
import { getISTTime } from '../constants.js';

export class NetworkError extends Error {
  constructor(message, code, originalError = null) {
    super(message);
    this.name = 'NetworkError';
    this.code = code;
    this.originalError = originalError;
    this.timestamp = getISTTime();
    this.isNetworkError = true;
  }
}

export class ErrorHandler {
  constructor(options = {}) {
    this.maxRetries = options.maxRetries || 3;
    this.baseDelay = options.baseDelay || 1000;
    this.maxDelay = options.maxDelay || 30000;
    this.onError = options.onError || (() => {});
    this.errorCounts = new Map();
  }

  async handleNetworkRequest(requestFn, context = '') {
    let lastError;
    
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = this.classifyError(error, context);
        
        if (attempt === this.maxRetries || !this.isRetryableError(lastError)) {
          this.recordError(lastError);
          throw lastError;
        }
        
        const delay = this.calculateBackoff(attempt);
        await this.sleep(delay);
      }
    }
    
    throw lastError;
  }

  classifyError(error, context) {
    // Network connectivity issues
    if (!navigator.onLine) {
      return new NetworkError('Device is offline', 'OFFLINE', error);
    }
    
    // Fetch API errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return new NetworkError('Network request failed', 'FETCH_ERROR', error);
    }
    
    // HTTP status errors
    if (error.message && error.message.includes('HTTP error')) {
      const statusMatch = error.message.match(/status: (\d+)/);
      const status = statusMatch ? parseInt(statusMatch[1]) : 0;
      
      if (status >= 500) {
        return new NetworkError(`Server error (${status})`, 'SERVER_ERROR', error);
      }
      if (status === 429) {
        return new NetworkError('Rate limited', 'RATE_LIMITED', error);
      }
      if (status >= 400) {
        return new NetworkError(`Client error (${status})`, 'CLIENT_ERROR', error);
      }
    }
    
    // Timeout errors
    if (error.name === 'AbortError' || error.message.includes('timeout')) {
      return new NetworkError('Request timeout', 'TIMEOUT', error);
    }
    
    // DNS/Connection errors
    if (error.message.includes('Failed to fetch') || 
        error.message.includes('ERR_NETWORK') ||
        error.message.includes('ERR_INTERNET_DISCONNECTED')) {
      return new NetworkError('Connection failed', 'CONNECTION_ERROR', error);
    }
    
    // Default network error
    const sanitizedContext = this.sanitizeContext(context);
    return new NetworkError('Network error in ' + sanitizedContext, 'UNKNOWN_NETWORK_ERROR', error);
  }

  isRetryableError(error) {
    const retryableCodes = [
      'OFFLINE',
      'FETCH_ERROR', 
      'SERVER_ERROR',
      'TIMEOUT',
      'CONNECTION_ERROR',
      'RATE_LIMITED'
    ];
    
    return error.isNetworkError && retryableCodes.includes(error.code);
  }

  calculateBackoff(attempt) {
    const exponentialDelay = this.baseDelay * Math.pow(2, attempt);
    const jitter = Math.random() * 0.1 * exponentialDelay;
    return Math.min(exponentialDelay + jitter, this.maxDelay);
  }

  recordError(error) {
    const key = error.code || 'UNKNOWN';
    this.errorCounts.set(key, (this.errorCounts.get(key) || 0) + 1);
    this.onError(error);
  }

  sanitizeContext(context) {
    if (typeof context !== 'string') return 'unknown';
    return context.replace(/[<>"'&]/g, (match) => {
      const entities = { '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#x27;', '&': '&amp;' };
      return entities[match];
    }).substring(0, 100);
  }

  getErrorStats() {
    return {
      errorCounts: Object.fromEntries(this.errorCounts),
      totalErrors: Array.from(this.errorCounts.values()).reduce((a, b) => a + b, 0)
    };
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  reset() {
    this.errorCounts.clear();
  }
}